import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react'
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react'
import { CONSUMABLE_SOURCE_LABELS, erfuelltRecht, type ConsumableView } from '@symphenia/shared-types'
import { KlappUmschalter, useKlappModus } from '@/components/klappmodus'
import { ZiehBereich, useRandMarkieren, useZeilenZiehen } from '@/components/ziehen/ZeilenZiehen'
import { verschiebeMehrereVor } from '@/components/ziehen/ziehen'
import { apiErrorText } from '@/lib/api-error'
import { api } from '@/lib/axios'
import { useAuthStore } from '@/lib/auth.store'
import { mitStand, useFeld, useGruppe, useSpeicherstelle, type Datensatz, type Gruppe, type Speicherstelle, type Zustand } from '@/lib/speichern'
import { fetchUnits } from '../api/project-bom.api'
import {
  createConsumable,
  deleteConsumable,
  fetchConsumableTrash,
  fetchConsumables,
  reorderConsumables,
  restoreConsumable,
  type ConsumableInput,
} from '../api/project-controlling.api'
import { searchArticles } from '../api/project-sales.api'
import { GeldFeld, geldEingabe } from './geldfeld'
import { PapierkorbFenster, RueckgaengigHinweis } from './Papierkorb'
import { Feld, RasterFeld, RasterInfo, Vorschlaege, ZeilenBlock, ZeilenListe, geldText, leseZahl, useListenBreite, useSuche, zahlText, zelle, type Spalte } from './zeilenblock'

/**
 * Nachtrag 15 (N15-17): Ab dieser **Listenbreite** stehen alle Felder in einer Zeile (am Nachbau abgestimmt) —
 * darunter bleibt die Anordnung aus Nachtrag 8.
 */
const BREIT_AB = 1518

const heute = () => new Date().toISOString().slice(0, 10)
const knopf = 'inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted'

type Artikel = { id: string; name: string; articleNumber: string; unit: { id: string; code: string } | null }
type Einheit = { id: string; code: string }

/**
 * AP-35 Detailblatt 7 4. — **Reiter Verbrauch an der Position.**
 *
 * Verbraucht, nicht beschafft: Kleinmaterial, Schrauben, Gas. Freie Texte aus
 * der Mitarbeiter-App tragen die Marke „Artikel zuordnen". Abgerechnet wird
 * über den Regievorschlag; danach ist die Zeile gesperrt, bis ihre
 * Verkaufszeile gelöscht wird.
 *
 * Erfasst und geändert wird im Zeilenblock (`zeilenblock.tsx`): jede Zeile
 * direkt bearbeitbar, unten immer eine leere. Seit Nachtrag 8 (19.9.2026)
 * wie die Stückliste: Zeilen stehen zugeklappt in **einer** Linie, alles
 * Weitere darunter; Beträge mit Tausenderpunkt und zwei Nachkommastellen; am linken Rand markieren, an
 * der freien Fläche greifen und verschieben (`components/ziehen`); Löschen
 * ohne Rückfrage in den Papierkorb, mit Rückgängig.
 *
 * AP-36 Phase F: Bestehende Zeilen speichern über den Baustein
 * `lib/speichern/` — eine Stelle je Zeile (`consumable:<id>`), die Zeile ist
 * ihre Gruppe. Die leere letzte Zeile legt weiter beim Verlassen an (Fall 2).
 *
 * Nachtrag 15 (N15-17, 11.10.2026): Ab 1.518 px **eigener** Breite steht alles in
 * einer Zeile — Artikelnummer, Notiz, Preiseinheit, Rabatt und „erfasst von" sind
 * Spalten, es gibt nichts mehr aufzuklappen. Darunter bleibt die Anordnung oben.
 */
export function PositionConsumablesTab({ projectId, positionId }: { projectId: string; positionId: string }) {
  const { user } = useAuthStore()
  const darf = (r: string) => erfuelltRecht(r, user?.permissions)
  const darfPflegen = darf('projects.sales.manage')
  const preise = darf('projects.price.view')

  const [zeilen, setZeilen] = useState<ConsumableView[] | null>(null)
  const [einheiten, setEinheiten] = useState<Einheit[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [neuSchluessel, setNeuSchluessel] = useState(0)
  const [neuFokus, setNeuFokus] = useState(false)
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  /** Zuletzt gelöscht — „Rückgängig“ holt den jüngsten zurück. */
  const [geloescht, setGeloescht] = useState<{ trashId: string; name: string }[]>([])
  const [papierkorbOffen, setPapierkorbOffen] = useState(false)
  // Auftraggeber 10.10.2026: Zeilen immer aufgeklappt, immer zugeklappt oder beim Bearbeiten — je Benutzer gemerkt.
  const [klappModus, setKlappModus] = useKlappModus('project_consumables')
  const behaelter = useRef<HTMLDivElement>(null)
  // Nachtrag 15 (N15-17): Die Liste richtet sich nach ihrer eigenen Breite, nicht nach dem Fenster.
  const { ref: listenRef, breite } = useListenBreite()
  const breit = breite >= BREIT_AB

  const laden = useCallback(async () => {
    try {
      setZeilen(await fetchConsumables(projectId, positionId))
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Der Verbrauch konnte nicht geladen werden.'))
    }
  }, [projectId, positionId])

  useEffect(() => {
    void laden()
    fetchUnits().then(setEinheiten).catch(() => undefined)
  }, [laden])

  const spalten = useMemo<Spalte[]>(
    () =>
      breit
        ? // Nachtrag 15 (N15-17): alles in einer Zeile. Die Breiten sind die des abgenommenen Nachbaus; was
          // übrig bleibt, teilen sich Artikel (3 Teile) und Notiz (2 Teile).
          [
            { key: 'am', label: 'am', spur: '8.5rem', min: 136, schmal: 'w-36' },
            { key: 'menge', label: 'Menge', spur: '5rem', min: 80, schmal: 'w-20', rechts: true },
            { key: 'einheit', label: 'Einheit', spur: '5rem', min: 80, schmal: 'w-24' },
            { key: 'name', label: 'Artikel oder freier Text', spur: 'minmax(12rem,3fr)', min: 192, schmal: 'w-72' },
            { key: 'nr', label: 'Artikel-Nr.', spur: '7rem', min: 112, schmal: 'w-28' },
            { key: 'notiz', label: 'Notiz', spur: 'minmax(10rem,2fr)', min: 160, schmal: 'w-72' },
            ...(preise ? [{ key: 'vk', label: 'VK', spur: '6.5rem', min: 104, schmal: 'w-28', rechts: true }] : []),
            ...(preise
              ? [
                  { key: 'pe', label: 'PE', spur: '3.5rem', min: 56, schmal: 'w-16', rechts: true },
                  { key: 'rabatt', label: 'Rab. %', spur: '3.75rem', min: 60, schmal: 'w-16', rechts: true },
                  { key: 'ek', label: 'EK', spur: '6rem', min: 96, schmal: 'w-24', rechts: true },
                ]
              : []),
            { key: 'herkunft', label: 'Herkunft', spur: '7rem', min: 112, schmal: 'w-28' },
            { key: 'von', label: 'erfasst von', spur: '8rem', min: 128, schmal: 'w-32' },
            { key: 'aktion', label: '', spur: '2rem', min: 32, schmal: 'w-8' },
          ]
        : [
            { key: 'am', label: 'am', spur: '8.5rem', min: 136, schmal: 'w-36' },
            { key: 'menge', label: 'Menge', spur: '5rem', min: 72, schmal: 'w-20', rechts: true },
            { key: 'einheit', label: 'Einheit', spur: '5rem', min: 72, schmal: 'w-24' },
            { key: 'name', label: 'Artikel oder freier Text', spur: 'minmax(12rem,1fr)', min: 192, schmal: 'w-72' },
            ...(preise
              ? [
                  { key: 'vk', label: 'VK', spur: '7rem', min: 104, schmal: 'w-28', rechts: true },
                  { key: 'ek', label: 'EK', spur: '6.5rem', min: 96, schmal: 'w-24', rechts: true },
                ]
              : []),
            { key: 'herkunft', label: 'Herkunft', spur: '8rem', min: 120, schmal: 'w-32' },
            { key: 'aktion', label: '', spur: '2rem', min: 28, schmal: 'w-8' },
          ],
    [preise, breit],
  )

  const liste = useMemo(() => zeilen ?? [], [zeilen])
  const reihe = useMemo(() => liste.map((z) => z.id), [liste])
  const auswahl = liste.filter((z) => markiert.has(z.id))

  /** Neue Reihenfolge an den Server; bis zur Antwort steht sie schon so da. */
  async function ordne(ids: readonly string[]) {
    if (!zeilen) return
    const nach = new Map(zeilen.map((z) => [z.id, z]))
    setZeilen(ids.map((id) => nach.get(id)!).filter(Boolean))
    try {
      setZeilen(await reorderConsumables(projectId, positionId, [...ids]))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Reihenfolge ließ sich nicht speichern.'))
      await laden()
    }
  }

  /** ↑ ↓ — die markierten Zeilen je um eine Stelle, Blöcke bleiben zusammen. */
  async function schiebe(richtung: -1 | 1) {
    if (!auswahl.length) return
    const ids = [...reihe]
    const r = richtung < 0 ? ids : ids.reverse()
    for (let i = 1; i < r.length; i++) {
      if (markiert.has(r[i]) && !markiert.has(r[i - 1])) [r[i - 1], r[i]] = [r[i], r[i - 1]]
    }
    await ordne(richtung < 0 ? r : [...r].reverse())
  }

  /** Mit der gegriffenen wandern alle markierten Zeilen — ist sie selbst markiert (AP-32 N-69 neu). */
  const mitGezogen = (key: string) => (markiert.has(key) ? reihe.filter((id) => markiert.has(id)) : [key])

  const { gezogen, ziel, dndProps } = useZeilenZiehen({
    behaelter,
    onAblegen: (key, vorKey) => {
      const neu = verschiebeMehrereVor(reihe, mitGezogen(key), vorKey)
      if (neu !== reihe) void ordne(neu)
    },
  })
  const { amRand } = useRandMarkieren({ behaelter, reihe, markiert, setMarkiert })

  /** Löschen ohne Rückfrage — es lässt sich zurücknehmen (Papierkorb, 30 Tage). Abgerechnete Zeilen bleiben. */
  async function loeschen(ziele: ConsumableView[]) {
    setFehler(null)
    const neu: { trashId: string; name: string }[] = []
    const fehlgeschlagen: string[] = []
    for (const z of ziele.filter((x) => !x.salesLineId)) {
      try {
        neu.push(await deleteConsumable(z.id))
      } catch (e) {
        fehlgeschlagen.push(`${z.name}: ${apiErrorText(e, 'nicht gelöscht')}`)
      }
    }
    setGeloescht((g) => [...g, ...neu])
    setMarkiert(new Set())
    if (fehlgeschlagen.length) setFehler(fehlgeschlagen.join(' · '))
    await laden()
  }

  async function wiederherstellen(trashId: string) {
    setFehler(null)
    try {
      setZeilen(await restoreConsumable(trashId))
      setGeloescht((g) => g.filter((x) => x.trashId !== trashId))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Zeile ließ sich nicht wiederherstellen.'))
    }
  }

  const summeVk = liste.reduce((s, z) => s + ((z.unitPrice ?? 0) * z.quantity * (1 - (z.discountPercent ?? 0) / 100)) / Math.max(1, z.priceUnit), 0)
  const summeEk = liste.reduce((s, z) => s + (z.purchasePrice ?? 0) * z.quantity, 0)

  // Die Breite wird am äußersten Element gemessen, und das steht **schon beim Laden** da: Die Messung hängt sich
  // einmal beim Einhängen an — ein Element, das erst mit den Daten erschiene, bliebe ungemessen (Breite 0, immer
  // die schmale Anordnung). Beide Rückgaben sind dasselbe `<div>` an derselben Stelle; React behält den Knoten.
  if (!zeilen) return <div ref={listenRef}>{fehler ? <p className="text-sm text-destructive">{fehler}</p> : <p className="text-sm text-muted-foreground">Lädt …</p>}</div>

  const gezogeneZeile = gezogen ? zeilen.find((z) => z.id === gezogen) : undefined
  const gezogeneIds = new Set(gezogen ? mitGezogen(gezogen) : [])
  const abgerechnetMarkiert = auswahl.filter((z) => z.salesLineId).length

  return (
    <div ref={listenRef} className="space-y-2">
      <div className="flex min-h-[2.125rem] flex-wrap items-center gap-2">
        <KlappUmschalter modus={klappModus} onModus={setKlappModus} />
        {darfPflegen &&
          (auswahl.length > 0 ? (
            <div className="ml-auto flex flex-wrap items-center gap-1 text-sm" data-auswahl-behalten>
              <span className="mr-1 text-muted-foreground">{auswahl.length} markiert</span>
              <button type="button" className="rounded border border-border p-1 hover:bg-muted" aria-label="nach oben" title="nach oben" onClick={() => void schiebe(-1)}>
                <ArrowUp size={14} />
              </button>
              <button type="button" className="rounded border border-border p-1 hover:bg-muted" aria-label="nach unten" title="nach unten" onClick={() => void schiebe(1)}>
                <ArrowDown size={14} />
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-destructive hover:bg-muted disabled:opacity-50"
                disabled={abgerechnetMarkiert === auswahl.length}
                title={abgerechnetMarkiert ? `${abgerechnetMarkiert} abgerechnete Zeilen bleiben stehen` : undefined}
                onClick={() => void loeschen(auswahl)}
              >
                <Trash2 size={13} /> löschen
              </button>
            </div>
          ) : (
            <div className="relative ml-auto">
              <button type="button" className={knopf} aria-expanded={papierkorbOffen} onClick={() => setPapierkorbOffen((x) => !x)}>
                <Trash2 size={14} /> Papierkorb
              </button>
              {papierkorbOffen && (
                <PapierkorbFenster
                  titel="Papierkorb des Verbrauchs"
                  laden={() => fetchConsumableTrash(projectId, positionId)}
                  onWiederherstellen={(id) => void wiederherstellen(id)}
                  onSchliessen={() => setPapierkorbOffen(false)}
                />
              )}
            </div>
          ))}
      </div>
      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      <RueckgaengigHinweis geloescht={geloescht} onRueckgaengig={(id) => void wiederherstellen(id)} onSchliessen={() => setGeloescht([])} />

      <div ref={behaelter}>
        <ZiehBereich
          dndProps={dndProps}
          vorschau={
            gezogeneZeile && (
              <>
                {gezogeneIds.size > 1 && <span className="mr-1 rounded bg-primary px-1.5 text-xs text-primary-foreground">{gezogeneIds.size} Zeilen</span>}
                <span className="tabular-nums text-muted-foreground">
                  {zahlText(gezogeneZeile.quantity)} {gezogeneZeile.unitCode}
                </span>{' '}
                <span className="font-medium">{gezogeneZeile.name}</span>
              </>
            )
          }
        >
          <ZeilenListe
            spalten={spalten}
            klappModus={klappModus}
            fuss={
              preise && zeilen.length > 0 ? (
                <div className="flex justify-between border-t-2 border-border bg-muted/40 px-3 py-1.5 text-xs font-medium">
                  <span>{zeilen.length} Zeilen</span>
                  <span className="tabular-nums">
                    EK {geldText(summeEk)} · VK {geldText(summeVk)}
                  </span>
                </div>
              ) : null
            }
          >
            {zeilen.length === 0 && !darfPflegen && <p className="px-3 py-6 text-center text-sm text-muted-foreground">Noch kein Verbrauch.</p>}
            {zeilen.map((z) => (
              <VerbrauchZeile
                key={z.id}
                z={z}
                einheiten={einheiten}
                preise={preise}
                breit={breit}
                darfPflegen={darfPflegen}
                markiert={markiert.has(z.id)}
                gezogen={gezogeneIds.has(z.id)}
                ziellinie={ziel?.key === z.id ? ziel.kante : null}
                onRand={darfPflegen ? amRand(z.id) : undefined}
                onGespeichert={laden}
                onLoeschen={() => void loeschen([z])}
              />
            ))}
            {darfPflegen && (
              <NeueVerbrauchZeile
                key={`neu-${neuSchluessel}`}
                einheiten={einheiten}
                preise={preise}
                breit={breit}
                autoFokus={neuFokus}
                onAnlegen={async (dto, perTastatur) => {
                  await createConsumable(projectId, positionId, dto as ConsumableInput & { name: string; quantity: number })
                  await laden()
                  setNeuFokus(perTastatur)
                  setNeuSchluessel((k) => k + 1)
                }}
              />
            )}
          </ZeilenListe>
        </ZiehBereich>
      </div>
    </div>
  )
}

// ── Zeile ─────────────────────────────────────────────────────────────────────

/**
 * Props eines Eingabefelds der Zeile. Bestehende Zeilen liefern sie aus dem
 * Speicherbaustein (`useFeld(...).props`), die leere letzte Zeile aus ihrem
 * lokalen Entwurf — die Darstellung darunter ist dieselbe.
 */
type Eingabe = {
  value: string
  disabled?: boolean
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
  name?: string
  'aria-invalid'?: boolean
  onBlur?: () => void
  onKeyDown?: (e: KeyboardEvent<HTMLElement>) => void
  ref?: (el: HTMLElement | null) => void
}

interface ZeilenFelder {
  deliveredAt: Eingabe
  quantity: Eingabe
  unitId: Eingabe
  name: Eingabe
  note: Eingabe
  priceUnit: Eingabe
  discountPercent: Eingabe
  /** Betrag: `GeldFeld` zeigt ihn anders an, als er bearbeitet wird — deshalb Text und Setzer statt `onChange`. */
  unitPrice: { value: string; disabled?: boolean; tippe: (text: string) => void }
}

interface Entwurf {
  deliveredAt: string
  quantity: string
  unitId: string
  name: string
  articleId: string | null
  articleNumber: string | null
  unitPrice: string
  priceUnit: string
  discountPercent: string
  note: string
}

function leererEntwurf(): Entwurf {
  return { deliveredAt: heute(), quantity: '', unitId: '', name: '', articleId: null, articleNumber: null, unitPrice: '', priceUnit: '1', discountPercent: '', note: '' }
}

/** Ein Wechsel nach `gespeichert` heißt: Der Server hat neue abgeleitete Werte (Summen, EK). */
function useNachSpeichern(zustand: Zustand, onGespeichert: () => void) {
  const vorher = useRef<Zustand>(zustand)
  useEffect(() => {
    if (zustand === 'gespeichert' && vorher.current !== 'gespeichert') onGespeichert()
    vorher.current = zustand
  }, [zustand, onGespeichert])
}

/** Bestehende Zeile: eine Speicherstelle, die Zeile ist ihre Gruppe (AP-36). */
function VerbrauchZeile({
  z,
  einheiten,
  preise,
  breit,
  darfPflegen,
  markiert,
  gezogen,
  ziellinie,
  onRand,
  onGespeichert,
  onLoeschen,
}: {
  z: ConsumableView
  einheiten: Einheit[]
  preise: boolean
  /** Die Liste ist breit genug für alles in einer Zeile (Nachtrag 15, N15-17). */
  breit: boolean
  darfPflegen: boolean
  markiert?: boolean
  gezogen?: boolean
  ziellinie?: 'oben' | 'unten' | null
  /** Druck auf den linken Rand — markieren (AP-32 N-64 neu). */
  onRand?: (e: ReactMouseEvent) => void
  onGespeichert: () => void
  onLoeschen?: () => void
}) {
  const gesperrt = !darfPflegen || Boolean(z.salesLineId)
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `consumable:${z.id}`,
    bezeichnung: z.name,
    daten: z as unknown as Datensatz,
    schreibe: (patch, stand) => api.patch<Datensatz>(`/projects/consumables/${z.id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/projects/consumables/${z.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: z.name })

  const datum = useFeld(stelle, 'deliveredAt', { gruppe, art: 'datum', pflicht: true, gesperrt, label: 'am' })
  const menge = useFeld<number | null>(stelle, 'quantity', { gruppe, art: 'zahl', pflicht: true, gesperrt, label: 'Menge' })
  const einheit = useFeld(stelle, 'unitId', { gruppe, art: 'auswahl', gesperrt, label: 'Einheit' })
  const name = useFeld<string | null>(stelle, 'name', { gruppe, pflicht: true, gesperrt, label: 'Bezeichnung', lies: (t) => t.trim() || null })
  const artikel = useFeld<string | null>(stelle, 'articleId', { gruppe, gesperrt, label: 'Artikel' })
  const notiz = useFeld<string | null>(stelle, 'note', { gruppe, gesperrt, label: 'Notiz', lies: (t) => t.trim() || null })
  // Der Betrag steht im Feld ohne Tausenderpunkt (`geldEingabe`), auch wenn er daneben anders aussieht.
  const preis = useFeld<number | null>(stelle, 'unitPrice', { gruppe, art: 'zahl', gesperrt, label: 'Verkaufspreis', schreib: (w) => geldEingabe(w) })
  const preisEinheit = useFeld<number>(stelle, 'priceUnit', { gruppe, art: 'zahl', gesperrt, label: 'Preiseinheit', lies: (t) => leseZahl(t) ?? 1 })
  const rabatt = useFeld<number | null>(stelle, 'discountPercent', { gruppe, art: 'zahl', stellen: 2, gesperrt, label: 'Rabatt %' })

  useNachSpeichern(gruppe.zustand, onGespeichert)

  /** Der eben gewählte Artikel trägt seine Nummer sofort, noch vor der Antwort des Servers. */
  const [gewaehlt, setGewaehlt] = useState<Artikel | null>(null)
  const artikelNummer = gewaehlt && gewaehlt.id === artikel.wert ? gewaehlt.articleNumber : z.articleNumber

  return (
    <VerbrauchZeileAnsicht
      z={z}
      einheiten={einheiten}
      preise={preise}
      breit={breit}
      darfPflegen={darfPflegen}
      gesperrt={gesperrt}
      gruppe={gruppe}
      markiert={markiert}
      gezogen={gezogen}
      ziellinie={ziellinie}
      onRand={onRand}
      onLoeschen={onLoeschen}
      artikelId={artikel.wert}
      artikelNummer={artikelNummer}
      onArtikelWahl={(a) => {
        setGewaehlt(a)
        name.tippe(a.name)
        artikel.tippe(a.id)
        // Die Einheit ist eine Auswahl und speichert sofort — sie nimmt Bezeichnung und Artikel mit.
        if (a.unit) einheit.tippe(a.unit.id)
      }}
      onVerwerfen={() => setGewaehlt(null)}
      felder={{
        deliveredAt: datum.props,
        quantity: menge.props,
        unitId: einheit.props,
        name: name.props,
        note: notiz.props,
        priceUnit: preisEinheit.props,
        discountPercent: rabatt.props,
        unitPrice: { value: preis.props.value, disabled: preis.props.disabled, tippe: preis.tippe },
      }}
    />
  )
}

/** Die leere letzte Zeile: legt beim Verlassen an (Fall 2 „Anlegen"). */
function NeueVerbrauchZeile({
  einheiten,
  preise,
  breit,
  autoFokus,
  onAnlegen,
}: {
  einheiten: Einheit[]
  preise: boolean
  breit: boolean
  autoFokus?: boolean
  onAnlegen: (dto: ConsumableInput, perTastatur: boolean) => Promise<void>
}) {
  const [w, setW] = useState<Entwurf>(leererEntwurf)
  const [fehler, setFehler] = useState<string | null>(null)
  const setze = (teil: Partial<Entwurf>) => setW((x) => ({ ...x, ...teil }))
  const feld = (k: 'deliveredAt' | 'quantity' | 'unitId' | 'name' | 'note' | 'unitPrice' | 'priceUnit' | 'discountPercent'): Eingabe => ({
    value: w[k],
    onChange: (e) => setze({ [k]: e.target.value } as Partial<Entwurf>),
  })

  /** Nur, was der Benutzer wirklich gefüllt hat — ein leeres Feld überschreibt die Vorgabe des Servers nicht. */
  function angaben(): ConsumableInput {
    const alt = leererEntwurf()
    const dto: ConsumableInput = {}
    if (w.articleId !== alt.articleId) dto.articleId = w.articleId
    if (w.unitId !== alt.unitId) dto.unitId = w.unitId || null
    if (w.note !== alt.note) dto.note = w.note.trim() || null
    if (preise) {
      if (leseZahl(w.unitPrice) !== leseZahl(alt.unitPrice)) dto.unitPrice = leseZahl(w.unitPrice)
      if (w.priceUnit !== alt.priceUnit) dto.priceUnit = leseZahl(w.priceUnit) ?? 1
      if (w.discountPercent !== alt.discountPercent) dto.discountPercent = leseZahl(w.discountPercent)
    }
    return dto
  }

  async function anlegen(perTastatur: boolean) {
    // Die neue Zeile entsteht erst, wenn sie eine Bezeichnung und eine Menge hat.
    const menge = leseZahl(w.quantity) ?? 1
    if (!w.name.trim() || !(menge > 0)) return
    try {
      setFehler(null)
      await onAnlegen({ ...angaben(), name: w.name.trim(), quantity: menge, deliveredAt: w.deliveredAt || heute() }, perTastatur)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Zeile ließ sich nicht speichern.'))
    }
  }

  return (
    <VerbrauchZeileAnsicht
      einheiten={einheiten}
      preise={preise}
      breit={breit}
      darfPflegen
      gesperrt={false}
      autoFokus={autoFokus}
      fehler={fehler}
      onVerlassen={(t) => void anlegen(t)}
      onVerwerfen={() => setW(leererEntwurf())}
      artikelId={w.articleId}
      artikelNummer={w.articleNumber}
      onArtikelWahl={(a) => setze({ name: a.name, articleId: a.id, articleNumber: a.articleNumber, ...(a.unit ? { unitId: a.unit.id } : {}) })}
      felder={{
        deliveredAt: feld('deliveredAt'),
        quantity: feld('quantity'),
        unitId: feld('unitId'),
        name: feld('name'),
        note: feld('note'),
        priceUnit: feld('priceUnit'),
        discountPercent: feld('discountPercent'),
        unitPrice: { value: w.unitPrice, tippe: (t) => setze({ unitPrice: t }) },
      }}
    />
  )
}

/** Die Darstellung — gleich für die bestehende und die leere Zeile. */
function VerbrauchZeileAnsicht({
  z,
  einheiten,
  preise,
  breit,
  darfPflegen,
  gesperrt,
  felder,
  artikelId,
  artikelNummer,
  onArtikelWahl,
  gruppe,
  onVerlassen,
  onVerwerfen,
  fehler,
  autoFokus,
  markiert,
  gezogen,
  ziellinie,
  onRand,
  onLoeschen,
}: {
  z?: ConsumableView
  einheiten: Einheit[]
  preise: boolean
  /**
   * Nachtrag 15 (N15-17): alles in einer Zeile. Artikelnummer, Notiz, Preiseinheit, Rabatt und „erfasst von"
   * stehen dann als Felder der ersten Zeile da — es gibt weder weitere Zeilen noch eine Kurzinfo.
   */
  breit: boolean
  darfPflegen: boolean
  gesperrt: boolean
  felder: ZeilenFelder
  artikelId: string | null
  artikelNummer: string | null
  onArtikelWahl: (a: Artikel) => void
  gruppe?: Gruppe
  onVerlassen?: (perTastatur: boolean) => void
  onVerwerfen?: () => void
  fehler?: string | null
  autoFokus?: boolean
  markiert?: boolean
  gezogen?: boolean
  ziellinie?: 'oben' | 'unten' | null
  onRand?: (e: ReactMouseEvent) => void
  onLoeschen?: () => void
}) {
  const [suchen, setSuchen] = useState(false)
  const [treffer, setTreffer] = useSuche<Artikel>(felder.name.value, suchen, searchArticles)
  const sucheBeenden = () => {
    setSuchen(false)
    setTreffer([])
  }

  const artikelHinweis = artikelNummer ? (
    <span className="font-mono">{artikelNummer}</span>
  ) : z?.needsArticle && !gesperrt ? (
    <span className="text-amber-700 dark:text-amber-400">freier Text — Artikel über die Suche zuordnen</span>
  ) : null
  const preisInfo = z && preise ? [z.priceUnit > 1 ? `PE ${z.priceUnit}` : '', z.discountPercent ? `Rabatt ${zahlText(z.discountPercent, 2)} %` : ''].filter(Boolean).join(' · ') : ''

  // Zugeklappt steht die erste Zeile in einer Linie; was sonst unter einem Feld
  // stünde (Artikelnummer, Notiz, Preiseinheit, erfasst von), steht darunter —
  // spaltengenau (Nachtrag 8).
  const zuInfo = z && (artikelHinweis || z.note || preisInfo || z.userName) && (
    <>
      <RasterInfo von="name">
        {artikelHinweis}
        {artikelHinweis && z.note ? ' · ' : ''}
        {z.note}
      </RasterInfo>
      {preise && (
        <RasterInfo von="vk" bis="ek" rechts>
          {preisInfo}
        </RasterInfo>
      )}
      <RasterInfo von="herkunft">{z.userName ?? ''}</RasterInfo>
    </>
  )

  const zeile2 = (
    <>
      <RasterFeld von="am" bis="einheit" label="Artikelnummer" schmal="w-56">
        <span className="block truncate px-1.5 py-1 text-xs text-muted-foreground">{artikelHinweis ?? '—'}</span>
      </RasterFeld>
      <RasterFeld von="name" label="Notiz" schmal="w-72">
        <input className={zelle} {...felder.note} />
      </RasterFeld>
      {preise && (
        <>
          <RasterFeld von="vk" label="Preiseinheit" schmal="w-24">
            <input className={`${zelle} text-right`} inputMode="numeric" {...felder.priceUnit} />
          </RasterFeld>
          <RasterFeld von="ek" label="Rabatt %" schmal="w-24">
            <input className={`${zelle} text-right`} inputMode="decimal" {...felder.discountPercent} />
          </RasterFeld>
        </>
      )}
      {z && (
        <RasterFeld von="herkunft" bis="aktion" label="erfasst von" schmal="w-32">
          <span className="block truncate px-1.5 py-1 text-xs text-muted-foreground">{z.userName ?? '—'}</span>
        </RasterFeld>
      )}
    </>
  )

  return (
    <ZeilenBlock
      neu={!z}
      autoFokus={autoFokus}
      schluessel={z?.id}
      ziehbar={Boolean(z && darfPflegen)}
      gezogen={gezogen}
      ziellinie={ziellinie}
      markiert={markiert}
      onRand={z ? onRand : undefined}
      randLabel={z?.name}
      gruppe={gruppe}
      onVerlassen={(t) => {
        sucheBeenden()
        onVerlassen?.(t)
      }}
      onVerwerfen={() => {
        sucheBeenden()
        onVerwerfen?.()
      }}
      fehler={fehler}
      gedimmt={Boolean(z?.salesLineId)}
      // Breit steht alles in der ersten Zeile (N15-17) — ohne `zeilen` gibt es auch nichts zuzuklappen.
      zeilen={breit ? undefined : [zeile2]}
      zuInfo={breit ? undefined : zuInfo || undefined}
      hinweis={z?.salesLineId ? 'Über den Regievorschlag abgerechnet — gesperrt, bis die Verkaufszeile gelöscht ist.' : undefined}
    >
      <Feld spalte="am">
        <input type="date" className={zelle} {...felder.deliveredAt} />
      </Feld>
      <Feld spalte="menge">
        <input className={`${zelle} text-right`} inputMode="decimal" placeholder={z ? '' : '1'} {...felder.quantity} />
      </Feld>
      <Feld spalte="einheit">
        <select className={zelle} {...felder.unitId}>
          <option value="">—</option>
          {einheiten.map((e) => (
            <option key={e.id} value={e.id}>
              {e.code}
            </option>
          ))}
        </select>
      </Feld>
      <Feld spalte="name">
        <div className="relative">
          <input
            data-start
            className={zelle}
            placeholder={z ? '' : 'neue Zeile — Artikel suchen oder Text eingeben'}
            {...felder.name}
            onChange={(e) => {
              felder.name.onChange(e)
              setSuchen(true)
            }}
          />
          <Vorschlaege
            allein
            eintraege={treffer}
            schluessel={(a) => a.id}
            onWahl={(a) => {
              onArtikelWahl(a)
              sucheBeenden()
            }}
            darstellung={(a) => (
              <>
                {a.name} <span className="font-mono text-[11px] text-muted-foreground">{a.articleNumber}</span>
              </>
            )}
          />
        </div>
      </Feld>
      {/* Nachtrag 15 (N15-17): Die Reihenfolge hier ist die Spalten- **und** die Tab-Reihenfolge —
          am → Menge → Einheit → Artikel → Notiz → VK → PE → Rabatt. */}
      {breit && (
        <Feld spalte="nr" leer={!artikelNummer && !z}>
          <span className="block truncate px-1.5 py-1 text-xs text-muted-foreground">
            {artikelNummer ? (
              <span className="font-mono">{artikelNummer}</span>
            ) : z?.needsArticle && !gesperrt ? (
              // Für den ganzen Hinweis ist die Spalte zu schmal — er steht im Tooltip.
              <span className="text-amber-700 dark:text-amber-400" title="freier Text — Artikel über die Suche zuordnen">
                freier Text
              </span>
            ) : z ? (
              '—'
            ) : (
              ''
            )}
          </span>
        </Feld>
      )}
      {breit && (
        <Feld spalte="notiz">
          <input className={zelle} aria-label="Notiz" {...felder.note} />
        </Feld>
      )}
      {preise && (
        <Feld spalte="vk">
          <GeldFeld
            className={zelle}
            disabled={felder.unitPrice.disabled}
            wert={felder.unitPrice.value}
            placeholder={artikelId ? 'Kundenpreis' : ''}
            aria-label="Verkaufspreis"
            onWert={felder.unitPrice.tippe}
          />
        </Feld>
      )}
      {breit && preise && (
        <>
          <Feld spalte="pe">
            <input className={`${zelle} text-right`} inputMode="numeric" aria-label="Preiseinheit" {...felder.priceUnit} />
          </Feld>
          <Feld spalte="rabatt">
            <input className={`${zelle} text-right`} inputMode="decimal" aria-label="Rabatt %" {...felder.discountPercent} />
          </Feld>
        </>
      )}
      {preise && (
        <Feld spalte="ek" leer={!z}>
          <span className="block px-1.5 py-1 text-right text-sm tabular-nums text-muted-foreground">{geldText(z?.purchasePrice)}</span>
        </Feld>
      )}
      <Feld spalte="herkunft" leer={!z}>
        <span className="block truncate px-1.5 py-1 text-xs text-muted-foreground">{z ? CONSUMABLE_SOURCE_LABELS[z.source] : ''}</span>
      </Feld>
      {breit && (
        <Feld spalte="von" leer={!z}>
          <span className="block truncate px-1.5 py-1 text-xs text-muted-foreground" title={z?.userName ?? undefined}>
            {z ? (z.userName ?? '—') : ''}
          </span>
        </Feld>
      )}
      <Feld spalte="aktion" leer={!z}>
        {z && onLoeschen && darfPflegen && !z.salesLineId ? (
          <button
            type="button"
            tabIndex={-1}
            className="p-1 text-muted-foreground hover:text-destructive"
            aria-label="löschen"
            title="löschen — lässt sich rückgängig machen"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onLoeschen}
          >
            <Trash2 size={14} />
          </button>
        ) : (
          <span />
        )}
      </Feld>
    </ZeilenBlock>
  )
}
